/**
 * A show-your-work task's progress on Hairo's entry points (the floating blob
 * and the sidebar "Ask Hairo" card) while the panel is closed and the task
 * runs in the background. Nothing renders without one (useCopilotTaskProgress
 * is null while the panel is open).
 *
 * Three states: working ("Step 2 of 5" with a ring), a step waiting for the
 * user's Confirm (never confirmed for them), and done. An ordinary answer
 * (kind "turn") shows "Working" and then "Answer ready".
 */
import { useCopilotTaskProgress } from "../CopilotProvider";

/** { label, tone, fraction } for a progress report, or null. */
export function taskBadgeOf(progress) {
  if (!progress) return null;
  // An ordinary answer running (or finished) while the panel was closed.
  if (progress.kind === "turn") {
    return progress.ended
      ? { label: "Answer ready", tone: "done", fraction: 1 }
      : { label: "Working", tone: "working", fraction: 0.25 };
  }
  const total = Math.max(0, Number(progress.total) || 0);
  const done = Math.min(total, Math.max(0, Number(progress.done) || 0));
  if (progress.ended) return { label: "Task done", tone: "done", fraction: 1 };
  if (progress.needsConfirm) {
    return { label: "Needs your confirm", tone: "confirm", fraction: total ? done / total : 0 };
  }
  const step = total ? Math.min(done + 1, total) : 0;
  return {
    label: total ? `Working: step ${step} of ${total}` : "Working",
    tone: "working",
    fraction: total ? done / total : 0,
  };
}

/** The ring around the orb: how much of the task is done. */
export function TaskRing({ size = 64 }) {
  const badge = taskBadgeOf(useCopilotTaskProgress());
  if (!badge) return null;
  const r = size / 2 - 3;
  const length = 2 * Math.PI * r;
  return (
    <svg
      className={`hairo-task-ring hairo-task-ring--${badge.tone}`}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
    >
      <circle className="hairo-task-ring__track" cx={size / 2} cy={size / 2} r={r} />
      <circle
        className="hairo-task-ring__fill"
        cx={size / 2}
        cy={size / 2}
        r={r}
        strokeDasharray={`${length * badge.fraction} ${length}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

/** The one-line status pill. `compact` for the collapsed rail (dot only). */
export default function TaskBadge({ compact = false, className = "" }) {
  const badge = taskBadgeOf(useCopilotTaskProgress());
  if (!badge) return null;
  return (
    <span
      className={`hairo-task-badge hairo-task-badge--${badge.tone}${
        compact ? " hairo-task-badge--compact" : ""
      }${className ? ` ${className}` : ""}`}
      role="status"
      aria-label={`Hairo: ${badge.label}`}
    >
      <span className="hairo-task-badge__dot" aria-hidden="true" />
      {compact ? null : <span className="hairo-task-badge__text">{badge.label}</span>}
    </span>
  );
}
